import { useState } from 'react';
import { 
  Table, 
  Tag, 
  Space, 
  Typography, 
  theme, 
  Tooltip, 
  Badge,
  Modal,
  Button
} from 'antd';
import {
  BuildOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  FileTextOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';
import { useJobModuleJobsQuery, type JobModuleJobListItem } from '../api';
import type { JobModuleJobSalary } from '../api/interfaces';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { DATE_FORMAT } from '@/utils/dateFormat';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { useToken } = theme;

export function JobsTable() {
  const { token } = useToken();
  const { t } = useTranslation('job-module');
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobModuleJobListItem | null>(null);
  
  const {
    data: jobsData,
    isLoading: loading,
    error
  } = useJobModuleJobsQuery();

  const handleViewDescription = (job: JobModuleJobListItem) => {
    setSelectedJob(job);
    setDescriptionModalOpen(true);
  };

  const handleCloseDescriptionModal = () => {
    setDescriptionModalOpen(false);
    setSelectedJob(null);
  };

  const formatSalary = (salary: JobModuleJobSalary) => {
    const value = salary.value;
    if (!value.value && !value.minValue && !value.maxValue) {
      return null;
    }
    const unit = value.unitText || 'year';
    const currency = salary.currency || 'USD';
    
    if (value.minValue && value.maxValue) {
      return `${currency} ${value.minValue.toLocaleString()} - ${value.maxValue.toLocaleString()} / ${unit}`;
    }
    if (value.value) {
      return `${currency} ${value.value.toLocaleString()} / ${unit}`;
    }
    return null;
  };

  const pinColumn = useResponsivePinnedColumn();
  const columns: ColumnsType<JobModuleJobListItem> = [
    {
      title: t('jobs.columns.jobDetails'),
      key: 'title',
      width: 320,
      fixed: pinColumn('left'),
      render: (_, record) => (
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Space size={8} align="start">
            <Space orientation="vertical" size={4} style={{ flex: 1 }}>
              <Space size={8} style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text strong style={{ fontSize: token.fontSizeLG, lineHeight: 1.4 }}>
                  {record.job.title}
                </Text>
                {record.job.description && (
                  <Tooltip title={t('jobs.viewDescription')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewDescription(record)}
                      style={{
                        padding: `0 ${token.paddingXS}px`,
                        height: 'auto',
                        color: token.colorPrimary
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
              <Space size={4}>
                <Tag 
                  color={record.metadata.active ? 'success' : 'default'} 
                  icon={record.metadata.active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  style={{ margin: 0 }}
                >
                  {record.metadata.active ? t('jobs.active') : t('jobs.inactive')}
                </Tag>
                {record.job.hiringOrganization.sameAs && (
                  <Tooltip title={t('jobs.website')}>
                    <Tag 
                      icon={<GlobalOutlined />}
                      style={{ cursor: 'pointer', margin: 0 }}
                      onClick={() => window.open(record.job.hiringOrganization.sameAs, '_blank')}
                    >
                      {t('jobs.website')}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            </Space>
          </Space>
        </Space>
      ),
      sorter: (a, b) => a.job.title.localeCompare(b.job.title)
    },
    {
      title: t('jobs.columns.organization'),
      key: 'organization',
      width: 200,
      render: (_, record) => (
        <Space size={8}>
          <BuildOutlined style={{ color: token.colorTextSecondary }} />
          <Space orientation="vertical" size={2}>
            <Text strong>{record.job.hiringOrganization.name}</Text>
            {record.job.hiringOrganization.sameAs && (
              <Text 
                type="secondary" 
                style={{ fontSize: token.fontSizeSM }}
                ellipsis
              >
                {new URL(record.job.hiringOrganization.sameAs).hostname}
              </Text>
            )}
          </Space>
        </Space>
      ),
      sorter: (a, b) => a.job.hiringOrganization.name.localeCompare(b.job.hiringOrganization.name)
    },
    {
      title: t('jobs.columns.location'),
      key: 'location',
      width: 280,
      render: (_, record) => {
        const locations = record.job.jobLocation;
        if (locations.length === 0) {
          return (
            <Text type="secondary">
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {t('jobs.remoteNotSpecified')}
            </Text>
          );
        }
        
        if (locations.length === 1) {
          const loc = locations[0];
          return (
            <Space orientation="vertical" size={4}>
              <Space size={4}>
                <EnvironmentOutlined style={{ color: token.colorTextSecondary }} />
                <Text strong>{loc.address.addressLocality}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {loc.address.streetAddress}
                {loc.address.postalCode && `, ${loc.address.postalCode}`}
              </Text>
              <Space size={4}>
                {loc.address.addressRegion && (
                  <Tag>{loc.address.addressRegion}</Tag>
                )}
                {loc.address.addressCountry && (
                  <Tag color="blue">{loc.address.addressCountry}</Tag>
                )}
              </Space>
            </Space>
          );
        }
        
        return (
          <Space orientation="vertical" size={4}>
            <Badge count={locations.length} showZero={false}>
              <Space size={4}>
                <EnvironmentOutlined style={{ color: token.colorTextSecondary }} />
                <Text strong>{locations.length} Location{locations.length > 1 ? 's' : ''}</Text>
              </Space>
            </Badge>
            <Tooltip 
              title={
                <div>
                  {locations.map((loc, idx) => (
                    <div key={idx} style={{ marginBottom: 4 }}>
                      {loc.address.addressLocality}, {loc.address.addressCountry}
                    </div>
                  ))}
                </div>
              }
            >
              <Text 
                type="secondary" 
                style={{ fontSize: token.fontSizeSM, cursor: 'pointer' }}
                ellipsis
              >
                {locations[0].address.addressLocality}
                {locations.length > 1 && ` +${locations.length - 1} more`}
              </Text>
            </Tooltip>
          </Space>
        );
      }
    },
    {
      title: t('jobs.columns.employmentType'),
      key: 'employmentType',
      width: 160,
      render: (_, record) => {
        const type = record.job.employmentType;
        const colorMap: Record<string, string> = {
          FULL_TIME: 'blue',
          PART_TIME: 'geekblue',
          CONTRACT: 'orange',
          TEMPORARY: 'cyan',
          INTERN: 'purple',
          VOLUNTEER: 'green'
        };
        const labelMap: Record<string, string> = {
          FULL_TIME: t('jobs.employmentTypes.FULL_TIME'),
          PART_TIME: t('jobs.employmentTypes.PART_TIME'),
          CONTRACT: t('jobs.employmentTypes.CONTRACT'),
          TEMPORARY: t('jobs.employmentTypes.TEMPORARY'),
          INTERN: t('jobs.employmentTypes.INTERN'),
          VOLUNTEER: t('jobs.employmentTypes.VOLUNTEER')
        };
        return (
          <Tag 
            color={colorMap[type] || 'default'}
          >
            {labelMap[type] || type.replace('_', ' ')}
          </Tag>
        );
      },
      filters: [
        { text: t('jobs.employmentTypes.FULL_TIME'), value: 'FULL_TIME' },
        { text: t('jobs.employmentTypes.PART_TIME'), value: 'PART_TIME' },
        { text: t('jobs.employmentTypes.CONTRACT'), value: 'CONTRACT' },
        { text: t('jobs.employmentTypes.TEMPORARY'), value: 'TEMPORARY' }
      ],
      onFilter: (value, record) => record.job.employmentType === value
    },
    {
      title: t('jobs.columns.postedDate'),
      key: 'datePosted',
      width: 180,
      render: (_, record) => {
        const postedDate = dayjs(record.job.datePosted);
        const isRecent = postedDate.isAfter(dayjs().subtract(7, 'days'));
        return (
          <Space orientation="vertical" size={2}>
            <Space size={4}>
              <CalendarOutlined style={{ color: token.colorTextSecondary }} />
              <Text>{postedDate.format(DATE_FORMAT)}</Text>
            </Space>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {isRecent && (
                <Tag color="green" style={{ margin: 0 }}>
                  {postedDate.fromNow()}
                </Tag>
              )}
              {!isRecent && postedDate.format('HH:mm')}
            </Text>
          </Space>
        );
      },
      sorter: (a, b) => dayjs(a.job.datePosted).unix() - dayjs(b.job.datePosted).unix(),
      defaultSortOrder: 'descend' as const
    },
    {
      title: t('jobs.columns.salary'),
      key: 'salary',
      width: 220,
      render: (_, record) => {
        const salaryText = formatSalary(record.job.baseSalary);
        if (!salaryText) {
          return (
            <Text type="secondary">
              <DollarOutlined style={{ marginRight: 4 }} />
              {t('jobs.notSpecified')}
            </Text>
          );
        }
        return (
          <Space size={4}>
            <DollarOutlined style={{ color: token.colorSuccess }} />
            <Text strong style={{ color: token.colorSuccess }}>
              {salaryText}
            </Text>
          </Space>
        );
      },
      sorter: (a, b) => {
        const aSalary = a.job.baseSalary.value;
        const bSalary = b.job.baseSalary.value;
        const aValue = aSalary.value || aSalary.minValue || 0;
        const bValue = bSalary.value || bSalary.minValue || 0;
        return Number(aValue) - Number(bValue);
      }
    },
  ];

  return (
    <div style={{ padding: token.paddingLG }}>
      {error && (
        <ErrorAlert
          title={t('jobs.errorLoading')}
          error={error}
          style={{ marginBottom: token.marginMD }}
        />
      )}
      <Table
        columns={columns}
        dataSource={jobsData?.jobs || []}
        rowKey={(record) => record.job['@type'] + '-' + record.job.title}
        loading={loading}
        pagination={{
          pageSize: 10,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('jobs.paginationTotal', { start: range[0], end: range[1], total }),
        }}
        scroll={{ x: 'max-content' }}
        size="middle"
        style={{
          background: token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          overflow: 'hidden'
        }}
      />

      {/* Description Modal */}
      <Modal
        title={
          <Space size={8}>
            <FileTextOutlined />
            <span>{t('jobs.jobDescription')}</span>
            {selectedJob && (
              <Text type="secondary" style={{ fontSize: token.fontSizeSM, fontWeight: 'normal' }}>
                - {selectedJob.job.title}
              </Text>
            )}
          </Space>
        }
        open={descriptionModalOpen}
        onCancel={handleCloseDescriptionModal}
        footer={[
          <Button key="close" onClick={handleCloseDescriptionModal}>
            {t('jobs.close')}
          </Button>
        ]}
        width={formModalWidth(800)}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: token.paddingLG
          }
        }}
      >
        {selectedJob?.job.description ? (
          <div
            dangerouslySetInnerHTML={{ __html: selectedJob.job.description }}
            style={{
              lineHeight: 1.6,
              fontSize: token.fontSize,
              color: token.colorText
            }}
          />
        ) : (
          <Text type="secondary">{t('jobs.noDescription')}</Text>
        )}
      </Modal>
    </div>
  );
}
